import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StepIndicator from '../onboarding/StepIndicator';
import ConnectorGrid from '../onboarding/ConnectorGrid';
import ConnectionForm from '../onboarding/ConnectionForm';
import TableSelector from '../onboarding/TableSelector';
import DictionaryStep from '../onboarding/DictionaryStep';
import TargetInputStep from './TargetInputStep';
import RunStep from './RunStep';
import type {
  OnboardingState, ConnectorType, TableInfo, DictionaryResult, MappingTargetMode,
} from '../../types';

interface Props {
  onClose: () => void;
}

const steps = [
  'Connect Source',
  'Source Tables',
  'Source Dictionary',
  'Target',
  'Run Mapping',
];

const blankOnboarding: OnboardingState = {
  step: 0,
  connectorType: null,
  connection: {},
  connectionTested: false,
  selectedSchema: null,
  selectedTables: [],
  columns: {},
  suggestedRules: [],
  domain: 'default',
  projectName: '',
  groupName: '',
  createAndRunResult: null,
  profileResult: null,
  dictionaryResult: null,
};

const MappingWizard: React.FC<Props> = ({ onClose }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  // Source side — mirrors OnboardingState shape so we can reuse ConnectionForm/TableSelector/DictionaryStep
  const [source, setSource] = useState<OnboardingState>(blankOnboarding);

  // Target side
  const [targetMode, setTargetMode] = useState<MappingTargetMode>('connection');
  const [target, setTarget] = useState<OnboardingState>(blankOnboarding);
  const [targetDictionary, setTargetDictionary] = useState<DictionaryResult | null>(null);

  // Config
  const [rateLimitRpm, setRateLimitRpm] = useState(30);
  const [description, setDescription] = useState('');

  // ── source setters ───────────────────────────────────────────
  const setSourceConnectorType = (connectorType: ConnectorType) =>
    setSource((s) => ({ ...s, connectorType, connection: { connector_type: connectorType } }));

  const setSourceConnection = (connection: OnboardingState['connection'], tested: boolean) =>
    setSource((s) => ({ ...s, connection, connectionTested: tested }));

  const setSourceSchema = (schema: string) => setSource((s) => ({ ...s, selectedSchema: schema }));
  const setSourceTables = (tables: TableInfo[]) => setSource((s) => ({ ...s, selectedTables: tables }));
  const setSourceColumns = (cols: Record<string, any>) => setSource((s) => ({ ...s, columns: cols }));
  const setSourceDictionary = (d: DictionaryResult) => setSource((s) => ({ ...s, dictionaryResult: d }));

  const next = () => setStep((s) => s + 1);
  const back = () => setStep((s) => Math.max(0, s - 1));

  return (
    <div>
      <StepIndicator steps={steps} current={step} />

      <div className="mt-8">
        {/* Step 0: Connect source */}
        {step === 0 && !source.connectorType && (
          <ConnectorGrid onSelect={setSourceConnectorType} selected={source.connectorType} />
        )}
        {step === 0 && source.connectorType && (
          <ConnectionForm
            state={source}
            setConnection={setSourceConnection}
            onNext={next}
            onBack={() => setSource((s) => ({ ...s, connectorType: null, connection: {} }))}
          />
        )}

        {/* Step 1: Select source tables */}
        {step === 1 && (
          <TableSelector
            state={source}
            setSchema={setSourceSchema}
            setTables={setSourceTables}
            setColumns={setSourceColumns}
            onNext={next}
            onBack={back}
          />
        )}

        {/* Step 2: Source dictionary (AI descriptions) */}
        {step === 2 && (
          <DictionaryStep
            state={source}
            setDictionaryResult={setSourceDictionary}
            onNext={next}
            onBack={back}
          />
        )}

        {/* Step 3: Target input */}
        {step === 3 && (
          <TargetInputStep
            mode={targetMode}
            setMode={setTargetMode}
            target={target}
            setTarget={setTarget}
            targetDictionary={targetDictionary}
            setTargetDictionary={setTargetDictionary}
            onNext={next}
            onBack={back}
          />
        )}

        {/* Step 4: Configure + run */}
        {step === 4 && (
          <RunStep
            source={source}
            targetMode={targetMode}
            target={target}
            targetDictionary={targetDictionary}
            rateLimitRpm={rateLimitRpm}
            setRateLimitRpm={setRateLimitRpm}
            description={description}
            setDescription={setDescription}
            onBack={back}
            onStarted={(runId) => navigate(`/mapping/runs/${runId}`)}
          />
        )}
      </div>
    </div>
  );
};

export default MappingWizard;
